import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { useMyDocuments } from '@/api/documents';
import { useCategoryLabel } from '@/api/categories';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import { ErrorMessage } from '@/components/shared/error-message';
import { EmptyState } from '@/components/shared/empty-state';
import { Pagination } from '@/components/shared/pagination';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/formatters';
import { FileText, Eye } from 'lucide-react';
import { SearchInput } from '@/components/shared/search-input';
import { useDocumentViewer } from '@/components/shared/use-document-viewer';
import type { DocumentCategory } from '@/types';
import { useDebounce } from '@/hooks/use-debounce';

const CATEGORY_OPTIONS: { label: string; value: DocumentCategory | undefined }[] = [
  { label: 'Todos', value: undefined },
  { label: 'Procedimiento', value: 'procedure' },
  { label: 'Política', value: 'policy' },
  { label: 'Formulario', value: 'form' },
  { label: 'Manual de calidad', value: 'quality_manual' },
  { label: 'Manual', value: 'manual' },
  { label: 'Instrucción', value: 'instruction' },
  { label: 'Registro', value: 'record' },
  { label: 'Otro', value: 'other' },
];

const CATEGORY_BADGES: Record<string, string> = {
  procedure: 'bg-blue-100 text-blue-700',
  policy: 'bg-green-100 text-green-700',
  form: 'bg-amber-100 text-amber-700',
  manual: 'bg-purple-100 text-purple-700',
  quality_manual: 'bg-purple-100 text-purple-700',
  instruction: 'bg-cyan-100 text-cyan-700',
  record: 'bg-gray-100 text-gray-600',
  other: 'bg-slate-100 text-slate-600',
};

export function DocumentsPage() {
  const categoryLabel = useCategoryLabel('document_category');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<DocumentCategory | undefined>(undefined);
  const [page, setPage] = useState(1);
  const { openDocument, viewer } = useDocumentViewer(); // #28/#23/#30 visor inline
  const debouncedSearch = useDebounce(search, 300);

  const {
    data: documentsRes,
    isLoading,
    error,
  } = useMyDocuments({
    search: debouncedSearch || undefined,
    category,
    page,
    per_page: 10,
  });

  const documents = documentsRes?.data ?? [];
  const pagination = documentsRes?.meta?.pagination;

  // #22: when arriving from a "siguiente paso" deep-link (?highlight=<id>),
  // highlight the target document and scroll it into view so it doesn't get
  // lost among the others.
  const [searchParams] = useSearchParams();
  const highlightId = Number(searchParams.get('highlight')) || 0;
  const highlightRef = useRef<HTMLTableRowElement>(null);
  useEffect(() => {
    if (highlightId && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightId, documents.length]);

  function handleCategoryChange(value: DocumentCategory | undefined) {
    setCategory(value);
    setPage(1);
  }

  function handleSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  return (
    <div className="space-y-6">
      {viewer}
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Mis Documentos</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Documentos vinculados a tus posiciones dentro de la organización.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput
          className="flex-1"
          value={search}
          onChange={handleSearchChange}
          placeholder="Buscar por código o título..."
        />
        <select
          value={category ?? ''}
          onChange={(e) =>
            handleCategoryChange((e.target.value || undefined) as DocumentCategory | undefined)
          }
          className="min-h-[44px] rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {CATEGORY_OPTIONS.map((opt) => (
            <option key={opt.label} value={opt.value ?? ''}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Content */}
      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorMessage message="No se pudieron cargar los documentos." />
      ) : documents.length === 0 ? (
        <EmptyState
          title="Sin documentos"
          description={
            debouncedSearch || category
              ? 'No se encontraron documentos con los filtros aplicados.'
              : 'No tienes documentos asignados actualmente.'
          }
        />
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-xl border border-border bg-card md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Código</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Título</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Categoría
                  </th>
                  <th className="px-4 py-3 text-center font-medium text-muted-foreground">
                    Versión
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Fecha Efectiva
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {documents.map((doc) => (
                  <tr
                    key={doc.id}
                    ref={doc.id === highlightId ? highlightRef : undefined}
                    className={cn(
                      'transition-colors hover:bg-accent/50',
                      doc.id === highlightId && 'bg-primary/10 ring-2 ring-inset ring-primary',
                    )}
                  >
                    <td className="px-4 py-3 font-mono text-xs font-bold text-card-foreground">
                      {doc.code}
                    </td>
                    <td className="px-4 py-3 text-card-foreground">{doc.title}</td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-xs font-medium',
                          CATEGORY_BADGES[doc.category],
                        )}
                      >
                        {categoryLabel(doc.category)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-muted-foreground">
                      v{doc.current_version}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {formatDate(doc.effective_date)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {doc.file_url || doc.external_url ? (
                        <button
                          type="button"
                          onClick={() => openDocument(doc.id, doc.title)}
                          className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1 text-xs font-medium text-card-foreground transition-colors hover:bg-accent"
                        >
                          <Eye className="h-3 w-3" />
                          Ver
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {documents.map((doc) => (
              <div key={doc.id} className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-xs font-bold text-card-foreground">{doc.code}</p>
                    <h3 className="mt-1 text-sm font-semibold text-card-foreground">{doc.title}</h3>
                  </div>
                  <FileText className="h-5 w-5 shrink-0 text-muted-foreground" />
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-xs font-medium',
                      CATEGORY_BADGES[doc.category],
                    )}
                  >
                    {categoryLabel(doc.category)}
                  </span>
                  <span className="text-xs text-muted-foreground">v{doc.current_version}</span>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  Efectiva: {formatDate(doc.effective_date)}
                </div>
                {(doc.file_url || doc.external_url) && (
                  <button
                    type="button"
                    onClick={() => openDocument(doc.id, doc.title)}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-card-foreground transition-colors hover:bg-accent"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    Ver Documento
                  </button>
                )}
              </div>
            ))}
          </div>

          {pagination && <Pagination meta={pagination} onPageChange={setPage} />}
        </>
      )}
    </div>
  );
}
